import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import AppStoreLink from '../components/common/AppStoreLink';
import { getActiveTripSummary, listFinishedBookings } from '../services/api/bookings';
import { getCustomerAuthUser } from '../services/storage/customerFlow';
import { formatRand } from '../config/rateCard';
import { INSTANT_MAX_DISTANCE_KM } from '../config/bookingPolicy';
import {
  formatEta,
  formatRoute,
  formatScheduledAt,
  statusToHuman,
  tripHeadline,
} from '../utils/tripFormatting';
import type {
  ActiveInstantTripSummary,
  BookingSummary,
  UpcomingAssignmentTripSummary,
} from '../types/booking';
import '../styles/route.css';

/**
 * The signed-in customer's deliveries — read-only.
 *
 * This replaces the funnel's worst dead end. A customer who completed email,
 * face-liveness and phone verification previously arrived at a page announcing
 * that the thing they came for does not exist. They can now see their
 * deliveries and start a new one; only scheduling ahead still needs the app.
 *
 * Deliberately read-only: nothing here creates, cancels or pays for anything.
 * Every state the app treats as ordinary — no trips, a failed load, a trip
 * needing support — is a first-class state here rather than an empty screen.
 */

type LoadState = 'loading' | 'ready' | 'error';

const AccountPage: React.FC = () => {
  /**
   * Read as a primitive, deliberately.
   *
   * `getCustomerAuthUser()` re-parses localStorage on every render and returns
   * a fresh object, so depending on the object itself gives `load` a new
   * identity each render, which re-runs the effect, which sets state, which
   * renders again — an infinite loop that leaves the page stuck on "Loading".
   * A string id compares by value and settles immediately.
   */
  const userId = getCustomerAuthUser()?.id ?? null;
  const [state, setState] = useState<LoadState>('loading');
  const [activeTrips, setActiveTrips] = useState<ActiveInstantTripSummary[]>([]);
  const [upcomingTrips, setUpcomingTrips] = useState<UpcomingAssignmentTripSummary[]>([]);
  const [pastBookings, setPastBookings] = useState<BookingSummary[]>([]);

  const load = useCallback(async () => {
    setState('loading');

    try {
      const summary = await getActiveTripSummary();
      setActiveTrips(summary.activeTrips);
      setUpcomingTrips(summary.upcomingTrips);

      /* History is secondary: a failure to load past trips must not blank out
         the live ones, which are the reason someone opens this page. */
      if (userId != null) {
        try {
          setPastBookings(await listFinishedBookings(String(userId)));
        } catch {
          setPastBookings([]);
        }
      }

      setState('ready');
    } catch {
      setState('error');
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const hasNothing =
    state === 'ready' && !activeTrips.length && !upcomingTrips.length && !pastBookings.length;

  return (
    <div className="landing-shell">
      <Header />
      <main>
        <section className="sw-section">
          <div className="container">
            <div className="sw-section-head">
              <span className="sw-eyebrow">Your deliveries</span>
              <h1>Trips and history</h1>
              {/* This page only renders behind `CustomerRoute`, which redirects
                  home while web booking is off — so booking IS live for anyone
                  reading this, and the copy no longer sends them to the app. */}
              <p>
                Track what is moving now, review what you have already sent, and book your next
                delivery.
              </p>
            </div>

            {/* Both ways to start a delivery, side by side.

                Present regardless of load state: a customer who came here to
                book should not have to wait for their trip history to load
                first, and should still get the action when that history fails.

                "Schedule for later" goes to the app on purpose. The web only
                implements the instant path (`book/request`); scheduling needs
                `book/assign/task` plus the scheduled-fare endpoint, neither of
                which exists here. Sending it to the app is the honest option —
                a web control that opened a date picker leading nowhere would be
                exactly the broken promise this page was built to remove. The
                note below names the limit rather than making people discover it
                at the confirm step. */}
            <div className="sw-hero-actions">
              <Link className="sw-btn-primary" to="/#estimate">
                Book a delivery
              </Link>
              <AppStoreLink className="sw-btn-secondary" placement="account_schedule_for_later">
                Schedule for later
              </AppStoreLink>
            </div>
            <p className="sw-trip-note">
              Booking here dispatches a driver now, for routes under {INSTANT_MAX_DISTANCE_KM} km.
              Scheduling ahead and longer trips happen in the app.
            </p>

            {state === 'loading' ? <p className="sw-trip-note">Loading your trips…</p> : null}

            {state === 'error' ? (
              <div>
                <p className="sw-trip-alert">
                  We could not load your trips right now. Your deliveries are unaffected.
                </p>
                <button className="sw-btn-secondary" onClick={() => void load()} type="button">
                  Try again
                </button>
              </div>
            ) : null}

            {state === 'ready' ? (
              <>
                <TripGroup
                  emptyCopy="Nothing on the road right now."
                  title="In progress"
                  count={activeTrips.length}
                >
                  {activeTrips.map((trip) => (
                    <ActiveTripCard key={`${trip.bookingId}-${trip.driverId ?? 'none'}`} trip={trip} />
                  ))}
                </TripGroup>

                <TripGroup
                  emptyCopy="No scheduled trips coming up."
                  title="Scheduled"
                  count={upcomingTrips.length}
                >
                  {upcomingTrips.map((trip) => (
                    <UpcomingTripCard key={`${trip.bookingId}-${trip.scheduledAt}`} trip={trip} />
                  ))}
                </TripGroup>

                <TripGroup
                  emptyCopy="Completed trips will appear here."
                  title="Past trips"
                  count={pastBookings.length}
                >
                  {pastBookings.map((booking) => (
                    <PastBookingCard booking={booking} key={booking.id} />
                  ))}
                </TripGroup>

                {/* No actions repeated here. Both ways out — book now, or
                    schedule in the app — sit above and are on screen whatever
                    the load state, so an empty state that listed them again
                    would just be the same two buttons twice. */}
                {hasNothing ? (
                  <p className="sw-trip-note">
                    Nothing here yet. Book a delivery above and it will show up as it moves.
                  </p>
                ) : null}
              </>
            ) : null}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

const TripGroup: React.FC<{
  title: string;
  count: number;
  emptyCopy: string;
  children: React.ReactNode;
}> = ({ title, count, emptyCopy, children }) => (
  <section className="sw-trip-group">
    <h2 className="sw-trip-group-title">
      {title}
      {/* The count sits beside the heading rather than inside it. At heading
          weight it read as part of the lane's name. */}
      {count > 0 ? <span className="sw-trip-group-count">{count}</span> : null}
    </h2>
    {count === 0 ? (
      <p className="sw-trip-note">{emptyCopy}</p>
    ) : (
      <div className="sw-choice-grid">{children}</div>
    )}
  </section>
);

/** Shown whenever the backend marks a trip as needing attention. */
const StaleNotice: React.FC<{ stale: boolean; reason?: string | null }> = ({ stale, reason }) =>
  stale ? (
    <p className="sw-trip-alert">Trip needs support{reason ? `: ${reason}` : ''}</p>
  ) : null;

/**
 * Where a card's "View details" goes.
 *
 * BOTH KEYS TRAVEL. The record endpoint resolves a trip by either the trip id
 * or the booking reference, and every lane's rows carry both — so sending both
 * is what makes a row whose trip id the backend has since superseded still
 * open. On the live and scheduled summaries the reference is the field named
 * `bookingId`; it is the same human code (`BK100235`) that the past rows call
 * `code`, which is why they can share this.
 */
function recordPath(tripId: string, reference?: string | null): string {
  const query = reference ? `?code=${encodeURIComponent(reference)}` : '';
  return `/account/trips/${encodeURIComponent(tripId)}/details${query}`;
}

/**
 * The two actions a card can carry, on one line.
 *
 * "View details" is on all three lanes because the app puts it on all three;
 * tracking is only offered where there is something to track.
 */
const CardActions: React.FC<{ detailsTo: string; trackTo?: string }> = ({ detailsTo, trackTo }) => (
  <div className="sw-card-actions">
    <Link className="sw-link-action" to={detailsTo}>
      View details &rarr;
    </Link>
    {trackTo ? (
      <Link className="sw-link-action" to={trackTo}>
        Track &rarr;
      </Link>
    ) : null}
  </div>
);

const ActiveTripCard: React.FC<{ trip: ActiveInstantTripSummary }> = ({ trip }) => {
  const eta = formatEta(trip.etaSeconds);
  const route = formatRoute(trip.pickupAddress, trip.dropoffAddress);
  const id = trip.tripId || trip.bookingId;

  return (
    <div className="sw-audience-card">
      <span className="sw-pill sw-pill-live">{tripHeadline(trip.phase, trip.status)}</span>
      <h3 className="sw-choice-title">{route ?? 'Trip in progress'}</h3>
      <p className="sw-choice-copy">
        {trip.bookingId}
        {eta ? ` · Arriving in ${eta}` : ''}
      </p>
      <StaleNotice reason={trip.staleReason} stale={trip.stale} />
      {/* Both, as in the app: the live map answers "where is it now", the
          record answers "what is this delivery". Only live trips are
          trackable — a finished one has nothing to watch. */}
      <CardActions
        detailsTo={recordPath(id, trip.bookingId)}
        trackTo={`/account/trips/${encodeURIComponent(id)}`}
      />
    </div>
  );
};

const UpcomingTripCard: React.FC<{ trip: UpcomingAssignmentTripSummary }> = ({ trip }) => {
  const route = formatRoute(trip.pickupAddress, trip.dropoffAddress);

  return (
    <div className="sw-audience-card">
      <span className="sw-pill sw-pill-quiet">{formatScheduledAt(trip.scheduledAt)}</span>
      <h3 className="sw-choice-title">{route ?? 'Scheduled trip'}</h3>
      <p className="sw-choice-copy">
        {trip.bookingId} · {statusToHuman(trip.status)}
      </p>
      <StaleNotice reason={trip.staleReason} stale={trip.stale} />
      {/* No tracking link: a scheduled trip has no driver on the road yet, so
          the map would only report that it is not in progress. */}
      <CardActions detailsTo={recordPath(trip.tripId || trip.bookingId, trip.bookingId)} />
    </div>
  );
};

const PastBookingCard: React.FC<{ booking: BookingSummary }> = ({ booking }) => {
  const route = formatRoute(booking.pickupShort, booking.dropoffShort);
  /* Prefer what was actually charged; fall back to the estimate only if the
     final figure is absent, and never invent one. */
  const amount = booking.fareAmount ?? booking.estimatedFare ?? null;

  return (
    <div className="sw-audience-card">
      <span className="sw-pill sw-pill-quiet">{statusToHuman(booking.status)}</span>
      <h3 className="sw-choice-title">{route ?? booking.code}</h3>
      <p className="sw-choice-copy">
        {formatScheduledAt(booking.scheduledAt ?? booking.createdAt)}
        {amount != null ? ` · ${formatRand(Math.round(amount))}` : ''}
      </p>
      {/* The app puts "View Details" on every past card; without it the web
          list is a dead end holding two lines of a trip it will not show. */}
      <CardActions detailsTo={recordPath(booking.tripId || booking.id, booking.code)} />
    </div>
  );
};

export default AccountPage;
